import {Alert, Badge, Button, Card, Group, Paper, SimpleGrid, Stack, Text, Title} from "@mantine/core";
import {useQuery} from "@tanstack/react-query";
import {CrownIcon} from "@phosphor-icons/react";
import {useEffect, useMemo} from "react";
import {useNavigate} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import api from "../../../utils/api.ts";
import {ProductionModuleInfo, ProductionModuleListResponse} from "./types.ts";

function ProductionModulesPage() {
  const navigate = useNavigate();

  const modulesQuery = useQuery<ProductionModuleInfo[]>({
    queryKey: ['modules', 'production'],
    queryFn: async () => {
      const response = await api.get('/modules/production');
      return (response.data as ProductionModuleListResponse).modules ?? [];
    },
  });

  const modules = useMemo(() => {
    const data = modulesQuery.data ?? [];
    return [...data].sort((a, b) => a.label.localeCompare(b.label));
  }, [modulesQuery.data]);

  useEffect(() => {
    if (modules.length === 1) {
      navigate(`/modules/production/${modules[0].id}/${modules[0].expired ? 'settings' : 'dashboard'}`)
    }
  }, [modules.length]);

  if (modulesQuery.isPending || modulesQuery?.data?.length === 1) {
    return <LoaderScreen message="Loading production modules"/>;
  }

  if (modulesQuery.isError) {
    return (
      <Page>
        <Alert color="red" title="Could not load modules">
          The production modules list is currently unavailable.
        </Alert>
      </Page>
    );
  }

  return (
    <Page>
      <title>Production Modules</title>
      <Stack gap="md">
        <div>
          <Title order={1}>Production Modules</Title>
          <Text c="dimmed">
            Select a module workspace. Every production request is module-scoped by id.
          </Text>
        </div>

        {modules.length === 0 ? (
          <Paper withBorder p="lg">
            <Text fw={600}>You currently do not have access to any production modules.</Text>
          </Paper>
        ) : null}

        <SimpleGrid cols={{base: 1, md: 3, lg: 5}}>
          {modules.map((module) => (
            <Card key={module.id} withBorder p="md">

              <div>
                <Group gap="xs">
                  <Text fw={700}>{module.label || `Production ${module.id}`}</Text>
                  {module.expired ? <Badge color="red" variant="light">Expired</Badge> : null}
                </Group>
                <Text size="sm" c="dimmed">
                  Access members: {module.accessCount}
                </Text>
                {module.isOwner ? <Group gap={2}><CrownIcon color="var(--mantine-color-yellow-2)"/><Text size="sm" c="dimmed">Owner</Text></Group> : null}
                <Text size="sm" c="dimmed">

                </Text>

              </div>


              <div style={{flexGrow: 1, minHeight: '1em'}}/>

              <Button
                onClick={() => navigate(`/modules/production/${module.id}/${module.expired ? 'settings' : 'dashboard'}`)}
                fullWidth
              >
                Open Module
              </Button>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>
    </Page>
  );
}

export default withRouteErrorBoundary(ProductionModulesPage);
